import React from "react";
import { Badge } from "@/components/ui/badge";
import { Opportunity, OpportunityCategory, getEffectiveSeverity, opportunityCategorySeverityBorderClass } from "../opportunity";
import { getCategoryColor } from "./utils";
import { Button } from "@/components/ui/button";
import { ColumnDef } from "@tanstack/react-table";

type OpportunitySortingOption = {
  value: string;
  label: string;
}

const LOCATION: OpportunitySortingOption = {
  value: "installation__location__name",
  label: "Location"
}
const CATEGORY: OpportunitySortingOption = {
  value: "category__name",
  label: "Category"
}
const CATEGORY_SEVERITY: OpportunitySortingOption = {
  value: "category__severity",
  label: "Category severity"
}
const IDENTIFIED_DATE: OpportunitySortingOption = {
  value: "identified_date",
  label: "Identified Date"
}
const LAST_DETECTED_DATE: OpportunitySortingOption = {
  value: "last_detected_date",
  label: "Last Detected Date"
}
const NUMBER_OF_DETECTIONS: OpportunitySortingOption = {
  value: "number_of_detections",
  label: "Number of Detections"
}
const FRP_USD: OpportunitySortingOption = {
  value: "max_saving_frp_usd",
  label: "FRP"
}
const FRP_PERCENTAGE: OpportunitySortingOption = {
  value: "max_saving_frp_percentage",
  label: "FRP %"
}
const ERP_TONS: OpportunitySortingOption = {
  value: "max_saving_erp_tons",
  label: "ERP"
}
const ERP_PERCENTAGE: OpportunitySortingOption = {
  value: "max_saving_erp_percentage",
  label: "ERP %"
}
const LL97_USD: OpportunitySortingOption = {
  value: "max_saving_ll97_usd",
  label: "LL97 Savings"
}
const STATUS: OpportunitySortingOption = {
  value: "status",
  label: "Status"
}

export const ALL_SORTING_OPTIONS = [
  LOCATION,
  CATEGORY,
  CATEGORY_SEVERITY,
  IDENTIFIED_DATE,
  LAST_DETECTED_DATE,
  NUMBER_OF_DETECTIONS,
  FRP_USD,
  FRP_PERCENTAGE,
  ERP_TONS,
  ERP_PERCENTAGE,
  LL97_USD,
  STATUS
]

export const getColumns = ({
    categories,
    openChangeStatusModal,
    showChangeStatus,
}: {
  categories: OpportunityCategory[];
  openChangeStatusModal: (opportunity: Opportunity) => void;
  showChangeStatus: boolean;
}) => {
  const columns: ColumnDef<Opportunity>[] = [
    {
      id: LOCATION.value,
      header: LOCATION.label,
      accessorKey: "location.name",
      enableSorting: true,
      cell: ({ row }: { row: { original: Opportunity } }) => {
        const { location, installation } = row.original;
        return (
          <div className="flex flex-col rounded-md">
            <div className="font-medium">{location.name}</div>
            <div className="text-sm">{installation.name}</div>
          </div>
        );
      },
    },
    {
      id: CATEGORY.value,
      header: CATEGORY.label,
      accessorKey: "category",
      cell: ({ row }: { row: { original: Opportunity } }) => {
        const category = row.original.category;
        const categoryColorClass = getCategoryColor(category, categories);
        return <Badge className="rounded-lg" customClass={categoryColorClass}>{category}</Badge>;
      },
    },
    {
      id: CATEGORY_SEVERITY.value,
      header: CATEGORY_SEVERITY.label,
      accessorFn: (row: Opportunity) => getEffectiveSeverity(row),
      cell: ({ row }: { row: { original: Opportunity } }) => {
        const severity = getEffectiveSeverity(row.original);
        if (!severity) {
          return "-"
        }

        return (
          <div className={
            `capitalize border-l-4 pl-2 ${opportunityCategorySeverityBorderClass[severity]}`
          }>
            {severity}
          </div>
        )
      },
    },
    { id: IDENTIFIED_DATE.value, header: IDENTIFIED_DATE.label, accessorKey: "identified_date" },
    {
      id: LAST_DETECTED_DATE.value,
      header: LAST_DETECTED_DATE.label,
      accessorFn: (row: Opportunity) => {
        if (!row.last_detected_date) return "";
        const date = new Date(row.last_detected_date);
        const year = date.getFullYear();
        return year >= 2020 ? date.toISOString().split("T")[0] : "";
      },
    },
    { id: NUMBER_OF_DETECTIONS.value, header: NUMBER_OF_DETECTIONS.label, accessorKey: "number_of_detections" },
    {
      id: FRP_USD.value,
      header: FRP_USD.label,
      accessorFn: (row: Opportunity) => {
        const savingsArray = Array.isArray(row.savings) ? row.savings : [];
        const totalFRPSavings = savingsArray
          .filter((saving) => saving.name.includes("FRP") && saving.unit === "$")
          .reduce((total, saving) => total + (saving.value || 0), 0);
        return totalFRPSavings !== 0 ? `$${totalFRPSavings}` : "-";
      },
    },
    {
      id: FRP_PERCENTAGE.value,
      header: FRP_PERCENTAGE.label,
      accessorFn: (row: Opportunity) => {
        const savingsArray = Array.isArray(row.savings) ? row.savings : [];
        const totalFRPSavings = savingsArray
          .filter((saving) => saving.name.includes("FRP") && saving.unit === "%")
          .reduce((total, saving) => total + (saving.value || 0), 0);
        return totalFRPSavings !== 0 ? `${totalFRPSavings} %` : "-";
      },
    },
    {
      id: ERP_TONS.value,
      header: ERP_TONS.label,
      accessorFn: (row: Opportunity) => {
        const savingsArray = Array.isArray(row.savings) ? row.savings : [];
        const totalERPSavings = savingsArray
          .filter((saving) => saving.name.includes("ERP") && saving.unit.includes("tons"))
          .reduce((total, saving) => total + (saving.value || 0), 0);
        return totalERPSavings !== 0 ? `${totalERPSavings} tons CO2e` : "-";
      },
    },
    {
      id: ERP_PERCENTAGE.value,
      header: ERP_PERCENTAGE.label,
      accessorFn: (row: Opportunity) => {
        const savingsArray = Array.isArray(row.savings) ? row.savings : [];
        const totalERPSavings = savingsArray
          .filter((saving) => saving.name.includes("ERP") && saving.unit === "%")
          .reduce((total, saving) => total + (saving.value || 0), 0);
        return totalERPSavings !== 0 ? `${totalERPSavings} %` : "-";
      },
    },
    {
      id: LL97_USD.value,
      header: LL97_USD.label,
      accessorFn: (row: Opportunity) => {
        const savingsArray = Array.isArray(row.savings) ? row.savings : [];
        const totalLL97Savings = savingsArray
          .filter((saving) => saving.name.includes("LL97") && saving.unit === "$")
          .reduce((total, saving) => total + (saving.value || 0), 0);
        return totalLL97Savings !== 0 ? `$${totalLL97Savings}` : "-";
      },
    },
    {
      id: STATUS.value,
      header: STATUS.label,
      accessorKey: "status",
      cell: ({ row }: { row: { original: Opportunity } }) => (
        <Badge variant="outline">{(row.original as any).status}</Badge>
      ),
    },
  ];

  if (showChangeStatus) {
    columns.push({
      id: "change_status",
      header: "Change status",
      cell: ({ row }: { row: { original: Opportunity } }) => {
        return (
          <Button variant="thalo" onClick={(e) => {
            e.stopPropagation();
            openChangeStatusModal(row.original);
          }}>
            Change status
          </Button>
        );
      },
    },)
  }
  return columns;
};

const columnsToHideInitially = [
  FRP_PERCENTAGE, FRP_USD, ERP_PERCENTAGE, ERP_TONS, LL97_USD
]

export const getInitialColumnVisibility = (columns: ColumnDef<Opportunity>[]) =>
  Object.fromEntries(
    columns.map(col => [
      col.id,
      !columnsToHideInitially.some(colToHide => colToHide.value === col.id)
    ])
  )
